import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    DataTable,
    Divider,
    EmptySearchResult,
    InlineGrid,
    InlineStack,
    Layout,
    Modal,
    Page,
    Spinner,
    Text,
    TextField,
    Tooltip,
} from '@shopify/polaris';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import {
    AtdDiagnosticActionResult,
    AtdPreviewDiagnosticRequest,
    AtdPreviewProductSearchItem,
} from '../../app/models/Atd';
import AtdTabs from './AtdTabs';

type AtdPreviewOrderLine = {
    id: string;
    productId?: number;
    partNumber: string;
    manufacturerPartNumber?: string;
    productName?: string;
    brandName?: string;
    categoryName?: string;
    primaryImageUrl?: string;
    availableQuantity?: number;
    quantity: number;
    isActive?: boolean;
    isDiscontinued?: boolean;
    isCustom?: boolean;
};

const defaultPreviewRequest: AtdPreviewDiagnosticRequest = {
    mode: 'valid',
    partNumber: '',
    quantity: 1,
    lines: [],
    customerPONumber: '',
    name: '',
    phoneNumber: '',
    emailAddress: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US',
    shippingMethod: 'CHEAPEST_FREIGHT',
};

const productImageStyle: React.CSSProperties = {
    width: 44,
    height: 44,
    borderRadius: 6,
    objectFit: 'cover',
    border: '1px solid var(--p-color-border-secondary)',
};

const placeholderImageStyle: React.CSSProperties = {
    ...productImageStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--p-color-bg-surface-secondary)',
};

const helpIconStyle: React.CSSProperties = {
    alignItems: 'center',
    border: '1px solid var(--p-color-border)',
    borderRadius: '50%',
    color: 'var(--p-color-text-subdued)',
    cursor: 'help',
    display: 'inline-flex',
    fontSize: 11,
    height: 16,
    justifyContent: 'center',
    lineHeight: '16px',
    width: 16,
};

const fillKillHelpText = 'ATD preview availability result. FILLED means ATD says the requested lines can currently be fulfilled; it does not mean an order was placed or inventory was reserved.';

const previewResultAnchorStyle: React.CSSProperties = {
    scrollMarginTop: 24,
};

const formatMoney = (value?: number) => value === undefined || value === null || Number.isNaN(value) ? '-' : `$${value.toFixed(2)}`;

const formatDate = (value?: string) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const getErrorMessage = (error: any, fallback: string) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim().length > 0) return data;
    if (data && typeof data === 'object') return JSON.stringify(data);
    return error?.message || fallback;
};

const getAny = (source: any, ...keys: string[]) => {
    for (const key of keys) {
        if (source?.[key] !== undefined && source?.[key] !== null) {
            return source[key];
        }
    }

    return undefined;
};

const toNumber = (value: any) => {
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? undefined : numberValue;
};

const parsePreviewSummary = (result?: AtdDiagnosticActionResult) => {
    if (!result?.detail) return undefined;

    try {
        const parsed = JSON.parse(result.detail);
        const order = getAny(parsed, 'order', 'Order');
        if (!order) return undefined;

        const lines = Array.isArray(getAny(order, 'orderLines', 'OrderLines')) ? getAny(order, 'orderLines', 'OrderLines') : [];
        const freight = lines.reduce((sum: number, line: any) => {
            const fulfillments = Array.isArray(getAny(line, 'fulfillments', 'Fulfillments')) ? getAny(line, 'fulfillments', 'Fulfillments') : [];
            return sum + fulfillments.reduce((lineSum: number, fulfillment: any) => lineSum + Number(getAny(fulfillment, 'freight', 'Freight') || 0), 0);
        }, 0);

        return {
            fillKill: getAny(order, 'fillKill', 'FillKill'),
            orderTotal: toNumber(getAny(order, 'orderTotal', 'OrderTotal')),
            freight,
            lines: lines.map((line: any) => ({
                cartLineNumber: getAny(line, 'cartLineNumber', 'CartLineNumber'),
                atdProductNumber: getAny(line, 'ATDProductNumber', 'atdProductNumber', 'AtdProductNumber'),
                description: getAny(line, 'description', 'Description'),
                quantity: getAny(line, 'quantity', 'Quantity'),
                cost: toNumber(getAny(line, 'cost', 'Cost')),
                fet: toNumber(getAny(line, 'fet', 'Fet')),
                lineTotal: toNumber(getAny(line, 'lineTotal', 'LineTotal')),
                freight: (Array.isArray(getAny(line, 'fulfillments', 'Fulfillments')) ? getAny(line, 'fulfillments', 'Fulfillments') : [])
                    .reduce((lineSum: number, fulfillment: any) => lineSum + Number(getAny(fulfillment, 'freight', 'Freight') || 0), 0),
                status: getAny((Array.isArray(getAny(line, 'fulfillments', 'Fulfillments')) ? getAny(line, 'fulfillments', 'Fulfillments') : [])[0], 'status', 'Status'),
                shipMethod: getAny((Array.isArray(getAny(line, 'fulfillments', 'Fulfillments')) ? getAny(line, 'fulfillments', 'Fulfillments') : [])[0], 'shipMethod', 'ShipMethod'),
                estimatedDelivery: getAny((Array.isArray(getAny(line, 'fulfillments', 'Fulfillments')) ? getAny(line, 'fulfillments', 'Fulfillments') : [])[0], 'estimatedDeliveryDate', 'EstimatedDeliveryDate', 'estimatedDelivery', 'EstimatedDelivery'),
                source: getAny((Array.isArray(getAny(line, 'fulfillments', 'Fulfillments')) ? getAny(line, 'fulfillments', 'Fulfillments') : [])[0], 'sourceDCName', 'SourceDCName', 'sourceDC', 'SourceDC'),
            })),
        };
    } catch {
        return undefined;
    }
};

const getPreviewMessage = (summary: ReturnType<typeof parsePreviewSummary>) => {
    if (!summary) {
        return undefined;
    }

    const status = summary.fillKill || 'preview result';
    return `ATD returned ${status} for ${summary.lines.length} line(s). Review freight, delivery, and source details below.`;
};

const getDisplayDetail = (detail?: string) => {
    if (!detail) return undefined;

    const trimmed = detail.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        return 'ATD returned a response, but it could not be summarized.';
    }

    return detail;
};

const normalizeQuantity = (value: string | number) => Math.max(1, Number(value) || 1);

const normalizePartNumber = (value?: string) => (value || '').trim().toLowerCase();

const ProductStatusBadge = ({
    isActive,
    isDiscontinued,
}: {
    isActive?: boolean;
    isDiscontinued?: boolean;
}) => {
    if (isDiscontinued) {
        return <Badge tone="critical">Discontinued</Badge>;
    }

    if (isActive === false) {
        return <Badge tone="attention">Inactive</Badge>;
    }

    return <Badge tone="success">Active</Badge>;
};

const ProductImage = ({
    source,
    alt,
}: {
    source?: string;
    alt: string;
}) => {
    if (source) {
        return <img src={source} alt={alt} style={productImageStyle} />;
    }

    return (
        <div style={placeholderImageStyle} aria-hidden="true">
            <Text as="span" variant="headingSm">ATD</Text>
        </div>
    );
};

const toOrderLine = (product: AtdPreviewProductSearchItem): AtdPreviewOrderLine => ({
    id: `product-${product.id}`,
    productId: product.id,
    partNumber: product.partNumber,
    manufacturerPartNumber: product.manufacturerPartNumber,
    productName: product.productName,
    brandName: product.brandName,
    categoryName: product.categoryName,
    primaryImageUrl: product.primaryImageUrl,
    availableQuantity: product.availableQuantity,
    quantity: 1,
    isActive: product.isActive,
    isDiscontinued: product.isDiscontinued,
});

const AtdPreviewPage: React.FC = () => {
    const navigate = useNavigate();
    const [request, setRequest] = useState<AtdPreviewDiagnosticRequest>(defaultPreviewRequest);
    const [orderLines, setOrderLines] = useState<AtdPreviewOrderLine[]>([]);
    const [productQuery, setProductQuery] = useState('');
    const [productSearchResults, setProductSearchResults] = useState<AtdPreviewProductSearchItem[]>([]);
    const [productSearchRan, setProductSearchRan] = useState(false);
    const [productSearchError, setProductSearchError] = useState<string | undefined>();
    const [searchingProducts, setSearchingProducts] = useState(false);
    const [productPickerOpen, setProductPickerOpen] = useState(false);
    const [pickerSelectedProductIds, setPickerSelectedProductIds] = useState<number[]>([]);
    const [customItemOpen, setCustomItemOpen] = useState(false);
    const [customPartNumber, setCustomPartNumber] = useState('');
    const [customQuantity, setCustomQuantity] = useState('1');
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState<AtdDiagnosticActionResult | undefined>();
    const previewResultRef = useRef<HTMLDivElement | null>(null);

    const summary = useMemo(() => parsePreviewSummary(result), [result]);

    useEffect(() => {
        if (!result) return;

        window.requestAnimationFrame(() => {
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            previewResultRef.current?.scrollIntoView({
                behavior: prefersReducedMotion ? 'auto' : 'smooth',
                block: 'start',
            });
        });
    }, [result]);

    const selectedPartNumbers = useMemo(
        () => new Set(orderLines.map(line => normalizePartNumber(line.partNumber)).filter(Boolean)),
        [orderLines]
    );

    const updateRequest = useCallback((field: keyof AtdPreviewDiagnosticRequest, value: string) => {
        setRequest(current => ({
            ...current,
            [field]: value,
        }));
    }, []);

    const searchProducts = useCallback(async (queryOverride?: string) => {
        try {
            setSearchingProducts(true);
            setProductSearchError(undefined);
            setProductSearchRan(true);
            const query = queryOverride ?? productQuery.trim();
            const results = await agent.Atd.previewProductSearch(query);
            setProductSearchResults(results);
        } catch (searchError: any) {
            setProductSearchResults([]);
            setProductSearchError(getErrorMessage(searchError, 'Unable to search ATD products.'));
        } finally {
            setSearchingProducts(false);
        }
    }, [productQuery]);

    const openProductPicker = useCallback(() => {
        setProductPickerOpen(true);
        setPickerSelectedProductIds([]);
        searchProducts(productQuery.trim());
    }, [productQuery, searchProducts]);

    const togglePickerProduct = useCallback((product: AtdPreviewProductSearchItem) => {
        if (selectedPartNumbers.has(normalizePartNumber(product.partNumber))) {
            return;
        }

        setPickerSelectedProductIds(current =>
            current.includes(product.id)
                ? current.filter(id => id !== product.id)
                : [...current, product.id]
        );
    }, [selectedPartNumbers]);

    const addSelectedProducts = useCallback(() => {
        const selectedProducts = productSearchResults
            .filter(product => pickerSelectedProductIds.includes(product.id))
            .filter(product => !selectedPartNumbers.has(normalizePartNumber(product.partNumber)));

        if (selectedProducts.length === 0) {
            return;
        }

        setResult(undefined);
        setOrderLines(current => [...current, ...selectedProducts.map(toOrderLine)]);
        setPickerSelectedProductIds([]);
        setProductPickerOpen(false);
    }, [pickerSelectedProductIds, productSearchResults, selectedPartNumbers]);

    const updateLineQuantity = useCallback((lineId: string, value: string) => {
        setOrderLines(current =>
            current.map(line =>
                line.id === lineId
                    ? { ...line, quantity: normalizeQuantity(value) }
                    : line
            )
        );
    }, []);

    const removeLine = useCallback((lineId: string) => {
        setResult(undefined);
        setOrderLines(current => current.filter(line => line.id !== lineId));
    }, []);

    const addCustomItem = useCallback(() => {
        const partNumber = customPartNumber.trim();
        if (!partNumber) {
            return;
        }

        const quantity = normalizeQuantity(customQuantity);
        const normalizedPartNumber = normalizePartNumber(partNumber);

        setResult(undefined);
        setOrderLines(current => {
            const existing = current.find(line => normalizePartNumber(line.partNumber) === normalizedPartNumber);
            if (existing) {
                return current.map(line =>
                    line.id === existing.id
                        ? { ...line, quantity: line.quantity + quantity }
                        : line
                );
            }

            return [
                ...current,
                {
                    id: `custom-${Date.now()}`,
                    partNumber,
                    productName: 'Custom ATD item',
                    quantity,
                    isCustom: true,
                },
            ];
        });
        setCustomPartNumber('');
        setCustomQuantity('1');
        setCustomItemOpen(false);
    }, [customPartNumber, customQuantity]);

    const runPreview = useCallback(async () => {
        const previewLines = orderLines
            .filter(line => line.partNumber.trim().length > 0)
            .map(line => ({
                partNumber: line.partNumber.trim(),
                quantity: normalizeQuantity(line.quantity),
            }));

        if (previewLines.length === 0) {
            return;
        }

        try {
            setRunning(true);
            setResult(undefined);
            setResult(await agent.Atd.diagnosticPreview({
                ...request,
                mode: 'valid',
                shippingMethod: 'CHEAPEST_FREIGHT',
                partNumber: previewLines[0].partNumber,
                quantity: previewLines[0].quantity,
                lines: previewLines,
            }));
        } catch (previewError: any) {
            setResult({
                success: false,
                message: 'ATD preview-order failed.',
                detail: getErrorMessage(previewError, 'ATD preview-order failed.'),
            });
        } finally {
            setRunning(false);
        }
    }, [orderLines, request]);

    const pickerSelectedProductCount = pickerSelectedProductIds.length;
    const canPreview = orderLines.length > 0;

    return (
        <>
            <Modal
                open={productPickerOpen}
                onClose={() => setProductPickerOpen(false)}
                title="Select products"
                primaryAction={{
                    content: pickerSelectedProductCount > 0 ? `Add ${pickerSelectedProductCount}` : 'Add',
                    onAction: addSelectedProducts,
                    disabled: pickerSelectedProductCount === 0,
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: () => setProductPickerOpen(false),
                }]}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        <InlineStack gap="200" blockAlign="end" wrap={false}>
                            <Box width="100%">
                                <TextField
                                    label="Search products"
                                    labelHidden
                                    value={productQuery}
                                    onChange={setProductQuery}
                                    autoComplete="off"
                                    placeholder="Search products"
                                />
                            </Box>
                            <Button onClick={() => searchProducts(productQuery.trim())} loading={searchingProducts}>Search</Button>
                        </InlineStack>

                        {productSearchError ? (
                            <Banner tone="critical" title="Product search failed">
                                <p>{productSearchError}</p>
                            </Banner>
                        ) : null}

                        {searchingProducts ? (
                            <InlineStack gap="200" blockAlign="center">
                                <Spinner accessibilityLabel="Searching ATD products" size="small" />
                                <Text as="p">Searching products...</Text>
                            </InlineStack>
                        ) : null}

                        {productSearchRan && productSearchResults.length === 0 && !searchingProducts && !productSearchError ? (
                            <EmptySearchResult title="No ATD products found" description="Try another part number, brand, category, or product name." withIllustration />
                        ) : null}

                        {productSearchResults.length > 0 ? (
                            <BlockStack gap="0">
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '40px minmax(0, 1fr) 110px 96px',
                                        gap: 12,
                                        padding: '8px 0',
                                        borderBottom: '1px solid var(--p-color-border-secondary)',
                                    }}
                                >
                                    <Text as="span" tone="subdued"> </Text>
                                    <Text as="span" tone="subdued">Product</Text>
                                    <Text as="span" tone="subdued">Available</Text>
                                    <Text as="span" tone="subdued">Status</Text>
                                </div>
                                {productSearchResults.map(product => {
                                    const alreadyAdded = selectedPartNumbers.has(normalizePartNumber(product.partNumber));
                                    const checked = pickerSelectedProductIds.includes(product.id);

                                    return (
                                        <div
                                            key={product.id}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '40px minmax(0, 1fr) 110px 96px',
                                                gap: 12,
                                                alignItems: 'center',
                                                padding: '12px 0',
                                                borderBottom: '1px solid var(--p-color-border-secondary)',
                                            }}
                                        >
                                            <Checkbox
                                                label={`Select ${product.productName || product.partNumber}`}
                                                labelHidden
                                                checked={checked || alreadyAdded}
                                                disabled={alreadyAdded}
                                                onChange={() => togglePickerProduct(product)}
                                            />
                                            <InlineStack gap="300" blockAlign="center" wrap={false}>
                                                <ProductImage source={product.primaryImageUrl} alt={product.productName || product.partNumber} />
                                                <BlockStack gap="050">
                                                    <Text as="p" variant="bodyMd">{product.productName || product.partNumber}</Text>
                                                    <Text as="p" tone="subdued">
                                                        {product.brandName || 'Unknown brand'} | ATD {product.partNumber}
                                                        {product.manufacturerPartNumber ? ` | MFG ${product.manufacturerPartNumber}` : ''}
                                                    </Text>
                                                    <Text as="p" tone="subdued">{product.categoryName || 'Uncategorized'}</Text>
                                                </BlockStack>
                                            </InlineStack>
                                            <Text as="span">{product.availableQuantity}</Text>
                                            {alreadyAdded ? <Badge>Added</Badge> : <ProductStatusBadge isActive={product.isActive} isDiscontinued={product.isDiscontinued} />}
                                        </div>
                                    );
                                })}
                            </BlockStack>
                        ) : null}
                    </BlockStack>
                </Modal.Section>
            </Modal>

            <Modal
                open={customItemOpen}
                onClose={() => setCustomItemOpen(false)}
                title="Add custom ATD item"
                primaryAction={{
                    content: 'Add item',
                    onAction: addCustomItem,
                    disabled: customPartNumber.trim().length === 0,
                }}
                secondaryActions={[{
                    content: 'Cancel',
                    onAction: () => setCustomItemOpen(false),
                }]}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        <Text as="p" tone="subdued">Use this when you already know the ATD part number but it is not showing in the cached product search.</Text>
                        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                            <TextField label="ATD part number" value={customPartNumber} onChange={setCustomPartNumber} autoComplete="off" />
                            <TextField label="Quantity" type="number" value={customQuantity} onChange={setCustomQuantity} autoComplete="off" />
                        </InlineGrid>
                    </BlockStack>
                </Modal.Section>
            </Modal>

            <Page
                title="ATD preview order"
                backAction={{ content: 'Integrations', onAction: () => navigate('/settings/integrations') }}
                primaryAction={{
                    content: 'Preview order',
                    onAction: runPreview,
                    loading: running,
                    disabled: running || !canPreview,
                }}
            >
                <Layout>
                    <Layout.Section>
                        <BlockStack gap="400">
                            <AtdTabs />

                            <Card>
                                <BlockStack gap="300">
                                    <BlockStack gap="100">
                                        <Text as="h2" variant="headingMd">Products</Text>
                                        <Text as="p" tone="subdued">Add one or more ATD products to preview fulfillment and freight.</Text>
                                    </BlockStack>

                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(260px, 620px) max-content max-content',
                                            gap: 8,
                                            alignItems: 'end',
                                        }}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            <TextField
                                                label="Search products"
                                                labelHidden
                                                value={productQuery}
                                                onChange={setProductQuery}
                                                autoComplete="off"
                                                placeholder="Search products"
                                            />
                                        </div>
                                        <Button onClick={openProductPicker}>Browse</Button>
                                        <Button onClick={() => setCustomItemOpen(true)}>Add custom item</Button>
                                    </div>

                                    {orderLines.length === 0 ? (
                                        <Box borderColor="border" borderWidth="025" borderRadius="200" padding="400">
                                            <Text as="p" tone="subdued">No products selected. Search or browse the cached ATD catalog to add products to the preview order.</Text>
                                        </Box>
                                    ) : (
                                        <DataTable
                                            columnContentTypes={['text', 'text', 'numeric', 'text', 'text']}
                                            headings={['Product', 'ATD part', 'Available', 'Qty', 'Action']}
                                            rows={orderLines.map(line => [
                                                <InlineStack key={`product-${line.id}`} gap="300" blockAlign="center" wrap={false}>
                                                    <ProductImage source={line.primaryImageUrl} alt={line.productName || line.partNumber} />
                                                    <BlockStack gap="050">
                                                        <InlineStack gap="200" blockAlign="center">
                                                            <Text as="p" variant="bodyMd">{line.productName || line.partNumber}</Text>
                                                            {line.isCustom ? <Badge>Custom</Badge> : <ProductStatusBadge isActive={line.isActive} isDiscontinued={line.isDiscontinued} />}
                                                        </InlineStack>
                                                        <Text as="p" tone="subdued">{line.brandName || 'Unknown brand'}{line.categoryName ? ` | ${line.categoryName}` : ''}</Text>
                                                    </BlockStack>
                                                </InlineStack>,
                                                line.partNumber || '-',
                                                line.availableQuantity ?? '-',
                                                <Box key={`qty-${line.id}`} width="88px">
                                                    <TextField
                                                        label={`Quantity for ${line.partNumber}`}
                                                        labelHidden
                                                        type="number"
                                                        value={String(line.quantity)}
                                                        min={1}
                                                        onChange={(value) => updateLineQuantity(line.id, value)}
                                                        autoComplete="off"
                                                    />
                                                </Box>,
                                                <Button key={`remove-${line.id}`} variant="plain" tone="critical" onClick={() => removeLine(line.id)}>Remove</Button>,
                                            ])}
                                        />
                                    )}
                                </BlockStack>
                            </Card>

                            <Card>
                                <BlockStack gap="300">
                                    <BlockStack gap="100">
                                        <Text as="h2" variant="headingMd">Order details</Text>
                                        <Text as="p" tone="subdued">Set the shipping method and optional customer purchase order.</Text>
                                    </BlockStack>
                                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                                        <Box borderColor="border" borderWidth="025" borderRadius="200" padding="300">
                                            <BlockStack gap="100">
                                                <Text as="p" tone="subdued">Shipping method</Text>
                                                <Text as="p" variant="headingSm">Cheapest freight</Text>
                                                <Text as="p" tone="subdued">ATD method: CHEAPEST_FREIGHT</Text>
                                            </BlockStack>
                                        </Box>
                                        <TextField label="Customer PO" value={request.customerPONumber || ''} onChange={(value) => updateRequest('customerPONumber', value)} autoComplete="off" />
                                    </InlineGrid>
                                </BlockStack>
                            </Card>

                            <Card>
                                <BlockStack gap="300">
                                    <BlockStack gap="100">
                                        <Text as="h2" variant="headingMd">Destination</Text>
                                        <Text as="p" tone="subdued">Enter the recipient address ATD should use for freight and fulfillment estimates.</Text>
                                    </BlockStack>
                                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
                                        <TextField label="Name" value={request.name || ''} onChange={(value) => updateRequest('name', value)} autoComplete="off" />
                                        <TextField label="Phone" value={request.phoneNumber || ''} onChange={(value) => updateRequest('phoneNumber', value)} autoComplete="off" />
                                        <TextField label="Email" value={request.emailAddress || ''} onChange={(value) => updateRequest('emailAddress', value)} autoComplete="off" />
                                        <TextField label="Address 1" value={request.address1 || ''} onChange={(value) => updateRequest('address1', value)} autoComplete="off" />
                                        <TextField label="Address 2" value={request.address2 || ''} onChange={(value) => updateRequest('address2', value)} autoComplete="off" />
                                        <TextField label="City" value={request.city || ''} onChange={(value) => updateRequest('city', value)} autoComplete="off" />
                                        <TextField label="State" value={request.state || ''} onChange={(value) => updateRequest('state', value)} autoComplete="off" />
                                        <TextField label="Postal code" value={request.postalCode || ''} onChange={(value) => updateRequest('postalCode', value)} autoComplete="off" />
                                        <TextField label="Country" value={request.country || ''} onChange={(value) => updateRequest('country', value)} autoComplete="off" />
                                    </InlineGrid>
                                    <InlineStack align="end">
                                        <Button variant="primary" onClick={runPreview} loading={running} disabled={running || !canPreview}>
                                            Preview order
                                        </Button>
                                    </InlineStack>
                                </BlockStack>
                            </Card>

                            {result ? (
                                <div ref={previewResultRef} style={previewResultAnchorStyle}>
                                    <BlockStack gap="400">
                                        <Banner tone={result.success ? 'success' : 'critical'} title={result.success ? 'Preview completed' : 'Preview failed'}>
                                            <p>{summary ? getPreviewMessage(summary) : result.message}</p>
                                            {!summary && getDisplayDetail(result.detail) ? <p>{getDisplayDetail(result.detail)}</p> : null}
                                        </Banner>

                                        {summary ? (
                                            <Card>
                                                <BlockStack gap="300">
                                                    <Text as="h2" variant="headingMd">Preview result</Text>
                                                    <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
                                                        <Box borderColor="border" borderWidth="025" borderRadius="200" padding="300">
                                                            <InlineStack gap="100" blockAlign="center">
                                                                <Text as="p" tone="subdued">Fill/kill</Text>
                                                                <Tooltip content={fillKillHelpText}>
                                                                    <span aria-label={fillKillHelpText} role="img" style={helpIconStyle} tabIndex={0}>?</span>
                                                                </Tooltip>
                                                            </InlineStack>
                                                            <Text as="p" variant="headingMd">{summary.fillKill || '-'}</Text>
                                                        </Box>
                                                        <Box borderColor="border" borderWidth="025" borderRadius="200" padding="300">
                                                            <Text as="p" tone="subdued">Order total</Text>
                                                            <Text as="p" variant="headingMd">{formatMoney(summary.orderTotal)}</Text>
                                                        </Box>
                                                        <Box borderColor="border" borderWidth="025" borderRadius="200" padding="300">
                                                            <Text as="p" tone="subdued">Freight</Text>
                                                            <Text as="p" variant="headingMd">{formatMoney(summary.freight)}</Text>
                                                        </Box>
                                                    </InlineGrid>
                                                    <Divider />
                                                    <DataTable
                                                        columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'text']}
                                                        headings={['Product', 'Status', 'Qty', 'Cost', 'Freight', 'Total', 'Delivery']}
                                                        rows={summary.lines.map((line: any) => [
                                                            <BlockStack key={`preview-line-${line.cartLineNumber || line.atdProductNumber}`} gap="050">
                                                                <Text as="p" variant="bodyMd">{line.atdProductNumber || '-'}</Text>
                                                                <Text as="p" tone="subdued">{line.description || '-'}</Text>
                                                                <Text as="p" tone="subdued">Line {line.cartLineNumber || '-'}</Text>
                                                            </BlockStack>,
                                                            line.status ? <Badge tone={line.status === 'FILLED' ? 'success' : 'attention'}>{line.status}</Badge> : '-',
                                                            line.quantity || '-',
                                                            formatMoney(line.cost),
                                                            formatMoney(line.freight),
                                                            formatMoney(line.lineTotal),
                                                            <BlockStack key={`delivery-${line.cartLineNumber || line.atdProductNumber}`} gap="050">
                                                                <Text as="p">{formatDate(line.estimatedDelivery)}</Text>
                                                                <Text as="p" tone="subdued">{line.shipMethod || '-'}</Text>
                                                                <Text as="p" tone="subdued">{line.source || '-'}</Text>
                                                            </BlockStack>,
                                                        ])}
                                                    />
                                                </BlockStack>
                                            </Card>
                                        ) : null}
                                    </BlockStack>
                                </div>
                            ) : null}
                        </BlockStack>
                    </Layout.Section>
                </Layout>
            </Page>
        </>
    );
};

export default AtdPreviewPage;
